import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Leaf } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(username.trim(), password);
      toast.success("Welcome back");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 grid lg:grid-cols-2 bg-bone-100 overflow-hidden">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-forest-600 text-bone-100 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, rgba(245,217,206,0.4), transparent 45%), radial-gradient(circle at 80% 60%, rgba(196,82,59,0.35), transparent 40%)",
          }}
        />
        <div className="relative flex items-center gap-3">
          <Leaf size={22} strokeWidth={1.75} />
          <div className="font-display text-2xl font-semibold tracking-tight">
            Payments
          </div>
        </div>
        <div className="relative">
          <div className="text-xs uppercase tracking-[0.3em] text-bone-100/70 mb-3">
            Order Ledger
          </div>
          <h1 className="font-display text-5xl font-semibold leading-[1.05] max-w-md">
            Every order.<br />
            Every rupee. Tracked.
          </h1>
          <p className="mt-5 text-bone-100/80 max-w-md">
            Record bills, route them to the right employee and party, and know
            exactly what&apos;s outstanding — colour-coded by age.
          </p>
        </div>
        <div className="relative text-xs text-bone-100/60 font-mono-num">
          © {new Date().getFullYear()}
        </div>
      </div>

      <div className="flex items-center justify-center p-6 overflow-hidden animate-fade-up">
        <form
          onSubmit={submit}
          className="w-full max-w-md card-surface p-8 space-y-5"
          data-testid="login-form"
        >
          <div>
            <div className="text-sm uppercase tracking-[0.3em] text-ink-muted">
              Access
            </div>
            <h2 className="font-display text-4xl font-semibold mt-2">Sign in</h2>
          </div>
          <div className="space-y-2">
            <Label
              htmlFor="username"
              className="text-sm uppercase tracking-wider text-ink-soft"
            >
              Username
            </Label>
            <Input
              id="username"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="your username"
              required
              className="hygenix-input h-11 text-base"
              data-testid="login-username-input"
            />
          </div>
          <div className="space-y-2">
            <Label
              htmlFor="password"
              className="text-sm uppercase tracking-wider text-ink-soft"
            >
              Password
            </Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="hygenix-input h-11 text-base"
              data-testid="login-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={busy}
            className="w-full btn-primary py-6 text-base"
            data-testid="login-submit-btn"
          >
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
